import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Mail, Phone, MapPin, Linkedin, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const HAS_BACKEND = Boolean(process.env.REACT_APP_BACKEND_URL);
const OWNER_EMAIL = "vjrajgopalan@hotmail.com";
const LINKEDIN_URL = "https://www.linkedin.com/in/vjrajgopalan/";

const buildMailto = (form) => {
  const subject = encodeURIComponent(
    `Excelsior Consulting inquiry — ${form.name || "New visitor"}`
  );
  const lines = [
    `Name: ${form.name}`,
    `Email: ${form.email}`,
    form.phone ? `Phone: ${form.phone}` : null,
    form.company ? `Organization: ${form.company}` : null,
    "",
    form.message,
  ].filter(Boolean);
  const body = encodeURIComponent(lines.join("\n"));
  return `mailto:${OWNER_EMAIL}?subject=${subject}&body=${body}`;
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);

    // Static deployment fallback: if no backend configured, open mail client
    if (!HAS_BACKEND) {
      window.location.href = buildMailto(form);
      toast.success("Opening your email app — please send the message to complete.");
      setSubmitting(false);
      return;
    }

    try {
      const { data } = await axios.post(`${API}/contact`, form, { timeout: 10000 });
      setSubmitted(true);
      toast.success(data?.message || "Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      // Fall back to mailto if backend is unreachable
      const mailto = buildMailto(form);
      toast.error("Could not reach the server — opening your email app instead.");
      window.location.href = mailto;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="container-app">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: heading + contact info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline-strong w-12" />
              <span className="label-eyebrow">05 · Engage</span>
            </div>
            <h2
              className="font-serif text-balance"
              style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", lineHeight: 1.05, letterSpacing: "-0.01em", fontWeight: 600 }}
              data-testid="contact-headline"
            >
              Plan your next
              <em className="italic font-normal" style={{ color: "#901A1E" }}> upgrade </em>
              or
              <em className="italic font-normal" style={{ color: "#901A1E" }}> migration</em>.
            </h2>
            <p className="mt-6 text-foreground/70 leading-relaxed" data-testid="contact-intro">
              Send a short note about your environment and timeline. You'll
              receive a reply — and a calendar link — within one business day.
            </p>

            <div className="mt-10 space-y-5" data-testid="contact-info">
              <a
                href="mailto:vjrajgopalan@hotmail.com"
                className="flex items-start gap-4 group"
                data-testid="contact-email-link"
              >
                <span className="mt-1 w-10 h-10 border border-border flex items-center justify-center group-hover:bg-foreground group-hover:text-background transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <div>
                  <div className="label-eyebrow">Email</div>
                  <div className="text-base mt-1">vjrajgopalan@hotmail.com</div>
                </div>
              </a>
              <a href="tel:+16172852331" className="flex items-start gap-4 group" data-testid="contact-phone-link">
                <span className="mt-1 w-10 h-10 border border-border flex items-center justify-center group-hover:bg-foreground group-hover:text-background transition-colors">
                  <Phone className="w-4 h-4" />
                </span>
                <div>
                  <div className="label-eyebrow">Direct</div>
                  <div className="text-base mt-1">617 · 285 · 2331</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <span className="mt-1 w-10 h-10 border border-border flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </span>
                <div>
                  <div className="label-eyebrow">Location</div>
                  <div className="text-base mt-1">Boston, Massachusetts</div>
                </div>
              </div>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-linkedin-link"
              >
                <span className="mt-1 w-10 h-10 border border-border flex items-center justify-center group-hover:bg-foreground group-hover:text-background transition-colors">
                  <Linkedin className="w-4 h-4" />
                </span>
                <div>
                  <div className="label-eyebrow">LinkedIn</div>
                  <div className="text-base mt-1">linkedin.com/in/vjrajgopalan</div>
                </div>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="border border-border bg-[#FBFAF6] p-8 md:p-10 space-y-6"
              data-testid="contact-form"
            >
              {submitted && (
                <div
                  className="flex items-center gap-3 text-sm bg-white border border-accent/30 text-foreground px-4 py-3"
                  data-testid="contact-success-banner"
                >
                  <CheckCircle2 className="w-4 h-4 text-accent" />
                  Message received. A response is on its way.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="label-eyebrow !text-foreground/60">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    className="mt-2 rounded-none border-0 border-b border-foreground/30 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent text-base h-11"
                    placeholder="Your full name"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="label-eyebrow !text-foreground/60">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    className="mt-2 rounded-none border-0 border-b border-foreground/30 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent text-base h-11"
                    placeholder="you@company.com"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="label-eyebrow !text-foreground/60">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="mt-2 rounded-none border-0 border-b border-foreground/30 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent text-base h-11"
                    placeholder="Optional"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="label-eyebrow !text-foreground/60">Organization</Label>
                  <Input
                    id="company"
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    className="mt-2 rounded-none border-0 border-b border-foreground/30 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent text-base h-11"
                    placeholder="Optional"
                    data-testid="contact-input-company"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="message" className="label-eyebrow !text-foreground/60">Project Brief</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  className="mt-2 rounded-none border-0 border-b border-foreground/30 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent text-base resize-none"
                  placeholder="A short description of your environment, current PeopleTools / app version, and what you're looking to accomplish."
                  data-testid="contact-input-message"
                />
              </div>
              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-foreground/50 max-w-sm">
                  By submitting, you consent to be contacted regarding this
                  inquiry. No data is shared with third parties.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-none bg-foreground text-background hover:bg-accent transition-colors h-auto px-8 py-4 text-xs tracking-[0.22em] uppercase font-medium disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send Inquiry <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
